import {ReactNode, useMemo} from 'react';

import {RootLayer} from '@sail/ui';

import {
  AppConfig,
  AppMessagePartHandlers,
  AppProfile,
  AppProvider,
  InlineDataProvider,
} from '@stripe-internal/conversations-assistant';
import {
  FeatureFlagsProvider as ConvUIFlagsProvider,
  type FeatureFlags as ConvUIIFlags,
  ThemeProvider as ConvUIThemeProvider,
  DefaultTheme,
  LinkTheme,
  type Theme,
} from '@stripe-internal/conversations-ui';
import {DefaultInlineDataComponentRegistry} from '@stripe-internal/mse-inline-data';
import {Observability} from '@stripe-internal/conversations-client';

const staticMobileConvUIFlags: Omit<ConvUIIFlags, 'enableUhPendingInput'> = {
  newUniversalHelpDesign: true,
  universalHelpDotComDesign: false,
  dotComFeedbackButtons: false,
  dotComNoLlm: false,
  dotComCsf: false,
  dotComCsfSelfServe: false,
  messagesTopAnchored: false,
  inlineRacEmailForm: true,
  conversationsLiveAnswersRow: true,
  universalHelpEncourageMoreDetailFromUserMse5042: false,
  enableScreenshareUi: false,
};

type Props = {
  config: AppConfig;
  profile: AppProfile;
  observability?: Observability;
  children: ReactNode;
};

function getThemeForAudience(audience: string): Theme {
  switch (audience) {
    case 'link':
      return LinkTheme;
    case 'stripe':
    default:
      return DefaultTheme;
  }
}

export function MobileAssistantProviders({
  config,
  profile,
  observability,
  children,
}: Props) {
  const mobileConvUIFlags = useMemo<ConvUIIFlags>(
    () => ({
      ...staticMobileConvUIFlags,
      enableUhPendingInput: !!profile.featureFlags.enableUhPendingInput,
    }),
    [profile.featureFlags.enableUhPendingInput],
  );

  const theme = useMemo(
    () => getThemeForAudience(config.audience),
    [config.audience],
  );

  const overrides = useMemo<Partial<Theme>>(
    () =>
      config.linkBrandName
        ? {
            linkBrandName: config.linkBrandName,
            assistantHeaderTitle: `${config.linkBrandName} Support`,
          }
        : {},
    [config.linkBrandName],
  );

  return (
    <ConvUIThemeProvider value={theme} overrides={overrides}>
      <RootLayer>
        <AppProvider
          config={config}
          profile={profile}
          observability={observability}
        >
          <AppMessagePartHandlers>
            <InlineDataProvider components={DefaultInlineDataComponentRegistry}>
              <ConvUIFlagsProvider value={mobileConvUIFlags}>
                {children}
              </ConvUIFlagsProvider>
            </InlineDataProvider>
          </AppMessagePartHandlers>
        </AppProvider>
      </RootLayer>
    </ConvUIThemeProvider>
  );
}
